<template>
  <div
    class="carousel-wrapper"
    :class="{ 'carousel-wrapper--dark': dark }"
    :style="{
      '--carouselSlideWidth': width,
      '--carouselSlideWidthDesktop': widthDesktop,
    }"
  >
    <div
      ref="carousel"
      class="carousel"
      :class="classes"
      @vnodeUpdated="handleSlotChange"
    >
      <slot />
    </div>
    <div v-if="!noControls" class="controls">
      <IconButton
        form="transparent"
        icon="arrow-left"
        class="control control--prev"
        :class="{ 'control--hidden': hidePrev }"
        :disabled="hidePrev"
        @click="handlePrevClick"
      />

      <IconButton
        form="transparent"
        icon="arrow-right"
        class="control control--next"
        :class="{ 'control--hidden': hideNext }"
        :disabled="hideNext"
        @click="handleNextClick"
      />
    </div>
  </div>
</template>

<script>
import IconButton from "@/components/IconButton.vue";

export default {
  name: "Carousel",
  components: {
    IconButton,
  },
  props: {
    dark: {
      type: Boolean,
      default: false,
    },
    width: {
      type: String,
      default: "55%",
    },
    widthDesktop: {
      type: String,
      default: "400px",
    },
    inline: {
      type: Boolean,
      default: false,
    },
    noControls: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      observer: null,
      indexOfVisible: 0,
      hidePrev: true,
      hideNext: false,
      intersectingEntries: [],
      lastEntry: null,
      items: [],
    };
  },
  computed: {
    classes() {
      return {
        "carousel--inline": this.inline,
      };
    },
  },
  mounted() {
    this.setupObserver();
  },
  unmounted() {
    this.observer.disconnect();
  },
  methods: {
    handlePrevClick() {
      this.items[this.indexOfVisible - 1].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
    },
    handleNextClick() {
      this.items[this.indexOfVisible + 1].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
    },
    handleSlotChange() {
      this.setupObserver();
    },
    setupObserver() {
      this.items = Array.from(this.$refs.carousel.children);
      let prevLeftMostIntersectedEntry = null;

      this.observer = new IntersectionObserver(
        (entries) => {
          [...entries].forEach((entry) => {
            if (entry.isIntersecting) {
              this.intersectingEntries.push(entry);
              entry.target.dataset.intersected = true;
            } else {
              const spliceIndex = this.intersectingEntries.findIndex((el) =>
                el.target.isSameNode(entry.target)
              );

              if (spliceIndex >= 0) {
                this.intersectingEntries.splice(spliceIndex, 1);
              }

              entry.target.dataset.intersected = false;
            }
          });

          Array.prototype.leftMostElement = function () {
            return (
              (this.length &&
                this.reduce(function (prev, curr) {
                  return prev.target.getBoundingClientRect().x <
                    curr.target.getBoundingClientRect().x
                    ? prev
                    : curr;
                })) ||
              null
            );
          };

          const leftMostIntsectedEntry =
            this.intersectingEntries.leftMostElement();

          if (prevLeftMostIntersectedEntry !== leftMostIntsectedEntry) {
            const target = leftMostIntsectedEntry?.target;
            const index =
              [...this.items].findIndex((el) => el.isSameNode(target)) || 0;

            this.indexOfVisible = index;
            prevLeftMostIntersectedEntry = leftMostIntsectedEntry;
          }

          const lastItem = [...this.items].at(-1);
          const lastItemIntersected = Boolean(
            lastItem.dataset.intersected === "true"
          );

          if (lastItemIntersected) {
            this.hideNext = true;
          } else {
            this.hideNext = false;
          }

          if (this.indexOfVisible === 0) {
            this.hidePrev = true;
          } else {
            this.hidePrev = false;
          }
        },
        {
          root: this.$refs.carousel,
          threshold: 0.5,
        }
      );

      for (const element of this.items) {
        this.observer.observe(element);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.carousel-wrapper {
  position: relative;

  .controls {
    display: flex;
    position: absolute;
    right: 0;
    top: -50px;
    display: none;

    @include respond-above(md) {
      display: block;
    }
  }
}

.carousel {
  display: flex;
  overflow-y: hidden;
  overflow-x: scroll;
  padding-left: $v-container-padding--mobile + 0px;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: top;
  gap: 16px;
  scroll-snap-type: x mandatory;
  width: auto;
  max-width: 100vw;

  @include respond-above(md) {
    padding-left: 140px;
  }

  &--inline {
    & > :slotted(*) {
      scroll-margin-left: 0;
    }

    @include respond-above(md) {
      padding-left: $v-container-padding--desktop + 0px;
    }
  }

  &::-webkit-scrollbar {
    display: none;
  }

  & > :slotted(*) {
    flex: 0 0 var(--carouselSlideWidth);
    scroll-snap-align: start;
    scroll-snap-stop: always;
    scroll-margin-left: 20px;

    @include respond-above(md) {
      flex: 0 0 var(--carouselSlideWidthDesktop);
      scroll-margin-left: 140px;
    }
  }
}

.control {
  padding: 10px;
  display: inline-block !important;

  .carousel-wrapper--dark & {
    color: $color-white;
  }

  &--hidden {
    opacity: 0.5;
  }
}
</style>
